import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock, LogOut, Plus, Pencil, Trash2, Loader2, Package, ArrowLeft, Search, Tag, Images, SwatchBook, Settings, ClipboardList, BarChart3,
} from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "../lib/api";
import { SECTORS, STORE, formatGs, sectorMeta } from "../data/store";
import { Field, Modal, FormActions, ImageUploader } from "../components/admin/shared";
import { SimpleManager } from "../components/admin/SimpleManager";
import { PROMOS_CONFIG, GALLERY_CONFIG, COLORS_CONFIG } from "../components/admin/configs";
import { SettingsManager } from "../components/admin/SettingsManager";
import { OrdersManager } from "../components/admin/OrdersManager";
import { SummaryManager } from "../components/admin/SummaryManager";

const EMPTY = {
  code: "", name: "", description: "", brand: "Sherwin-Williams", sector: "inmobiliario",
  category: "", price: 0, unit: "Unidad", stock: 0, image_url: "", featured: false, specs: "",
};

const TABS = [
  { key: "summary", label: "Resumen", icon: BarChart3 },
  { key: "orders", label: "Pedidos", icon: ClipboardList },
  { key: "products", label: "Productos", icon: Package },
  { key: "promotions", label: "Promociones", icon: Tag },
  { key: "gallery", label: "Galería", icon: Images },
  { key: "colors", label: "Colores", icon: SwatchBook },
  { key: "settings", label: "Ajustes", icon: Settings },
];

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.login(email, password);
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A3977] px-4 paint-drip-bg">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <button onClick={() => navigate("/")} data-testid="back-to-store" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Volver a la tienda
        </button>
        <div className="w-12 h-12 rounded-xl bg-[#0A3977] flex items-center justify-center mb-4">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Panel de Administración</h1>
        <p className="text-gray-500 text-sm mt-1">Ingresá para gestionar el catálogo.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-500">Email</label>
            <input
              data-testid="admin-email-input" type="email" value={email} required
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1]"
            />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-500">Contraseña</label>
            <input
              data-testid="admin-password-input" type="password" value={password} required
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1]"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600 font-medium">{error}</p>}
          <button
            data-testid="admin-login-button" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#E02424] text-white font-bold hover:bg-[#c01f1f] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Ingresar
          </button>
        </form>
      </div>
    </div>
  );
};

const ProductForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const editing = !!initial.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      code: form.code, name: form.name, description: form.description, brand: form.brand,
      sector: form.sector, category: form.category, price: Number(form.price),
      unit: form.unit, stock: Number(form.stock), image_url: form.image_url,
      featured: !!form.featured, specs: form.specs,
    };
    try {
      if (editing) await api.updateProduct(form.id, payload);
      else await api.createProduct(payload);
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? "Editar producto" : "Nuevo producto"} onClose={onClose} testId="product-form">
      <form onSubmit={save} className="p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Código"><input required data-testid="form-code" value={form.code} onChange={(e) => set("code", e.target.value)} className="inp" /></Field>
        <Field label="Marca"><input data-testid="form-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="inp" /></Field>
        <Field label="Nombre" full><input required data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" /></Field>
        <Field label="Descripción" full><textarea data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="inp" /></Field>
        <Field label="Sector">
          <select data-testid="form-sector" value={form.sector} onChange={(e) => set("sector", e.target.value)} className="inp">
            {SECTORS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Categoría"><input data-testid="form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="inp" /></Field>
        <Field label="Precio (₲)"><input required type="number" data-testid="form-price" value={form.price} onChange={(e) => set("price", e.target.value)} className="inp" /></Field>
        <Field label="Unidad"><input data-testid="form-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)} className="inp" /></Field>
        <Field label="Stock"><input type="number" data-testid="form-stock" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="inp" /></Field>
        <Field label="Especificaciones"><input data-testid="form-specs" value={form.specs} onChange={(e) => set("specs", e.target.value)} className="inp" /></Field>
        <div className="sm:col-span-2">
          <ImageUploader label="Foto del producto" value={form.image_url} onChange={(url) => set("image_url", url)} />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Destacado
        </label>
        <FormActions onClose={onClose} saving={saving} />
      </form>
    </Modal>
  );
};

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { setProducts(await api.listProducts()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try { await api.deleteProduct(p.id); toast.success("Producto eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = products.filter((p) =>
    [p.name, p.code, p.brand].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Gestión de productos</h1>
          <p className="text-gray-500 text-sm">{products.length} productos en el catálogo</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm outline-none" />
          </div>
          <button data-testid="add-product-btn" onClick={() => setEditing(EMPTY)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E02424] text-white font-bold text-sm hover:bg-[#c01f1f] transition-colors">
            <Plus className="w-4 h-4" /> Nuevo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Código</th>
                  <th className="text-left px-4 py-3">Producto</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Sector</th>
                  <th className="text-right px-4 py-3">Precio</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Stock</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p) => {
                  const meta = sectorMeta(p.sector);
                  return (
                    <tr key={p.id} data-testid={`admin-row-${p.code}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono-code text-xs font-bold text-gray-500">{p.code}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={assetUrl(p.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0" />
                          <div>
                            <div className="font-semibold text-gray-900 line-clamp-1">{p.name}</div>
                            <div className="text-xs text-gray-400">{p.brand}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: meta.bg, color: meta.text }}>{meta.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#E02424]">{formatGs(p.price)}</td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">{p.stock}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-${p.code}`} onClick={() => setEditing(p)} className="p-2 rounded-lg hover:bg-blue-50 text-[#0067B1]"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`delete-${p.code}`} onClick={() => remove(p)} className="p-2 rounded-lg hover:bg-red-50 text-[#E02424]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("summary");

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <style>{`.inp{width:100%;padding:0.55rem 0.75rem;border:1px solid #D1D5DB;border-radius:0.5rem;outline:none;background:#fff}.inp:focus{border-color:#0067B1}`}</style>
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#0A3977]" />
            <span className="font-display font-extrabold text-[#0A3977]">Panel · {STORE.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden sm:inline text-sm font-semibold text-gray-600 hover:text-gray-900">Ver tienda</a>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 font-semibold text-sm hover:bg-gray-50">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key ? "border-[#E02424] text-[#0A3977]" : "border-transparent text-gray-500 hover:text-gray-900"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {tab === "summary" && <SummaryManager />}
        {tab === "orders" && <OrdersManager />}
        {tab === "products" && <ProductsManager />}
        {tab === "promotions" && <SimpleManager config={PROMOS_CONFIG} />}
        {tab === "gallery" && <SimpleManager config={GALLERY_CONFIG} />}
        {tab === "colors" && <SimpleManager config={COLORS_CONFIG} />}
        {tab === "settings" && <SettingsManager />}
      </main>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api.me().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const logout = async () => { try { await api.logout(); } catch {} setAuthed(false); };

  if (authed === null)
    return <div className="min-h-screen flex items-center justify-center bg-[#0A3977]"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  if (!authed) return <Login onDone={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
